import { useEffect, useRef, useState } from "react";
import type { CustomLightingInfo } from "@eweadn-qiankun/shared-types";

export type XingshanStripRoutineBase = {
  open?: string;
  mode?: number;
  luminance?: number;
  speed?: number;
  direction?: string | number;
  selectStaticColor?: number;
};

type StripCell = CustomLightingInfo;

const RAINBOW_FLOW_STOPS: Array<{ h: number; s: number; v: number }> = [
  { h: 0, s: 0.92, v: 1 },
  { h: 45, s: 0.9, v: 1 },
  { h: 90, s: 0.88, v: 1 },
  { h: 150, s: 0.9, v: 1 },
  { h: 200, s: 0.92, v: 1 },
  { h: 260, s: 0.9, v: 1 },
  { h: 300, s: 0.88, v: 1 },
  { h: 360, s: 0.92, v: 1 },
];

export const XINGSHAN_STRIP_ROUTINE_MODES = [0, 1, 2, 3] as const;

export function isXingshanStripRoutineMode(mode: number): boolean {
  return (XINGSHAN_STRIP_ROUTINE_MODES as readonly number[]).includes(mode);
}

export function shouldUseXingshanStripRoutinePreview(
  provider: string | undefined,
  tab: string,
  base: XingshanStripRoutineBase | undefined | null
): boolean {
  if (String(provider ?? "") !== "xingshan") return false;
  if (tab !== "Decorate1") return false;
  if (base?.open === "Close") return false;
  return isXingshanStripRoutineMode(Number(base?.mode ?? -1));
}

function hsvToRgb(h: number, s: number, v: number): StripCell {
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;
  let r = 0;
  let g = 0;
  let b = 0;
  if (h < 60) {
    r = c;
    g = x;
  } else if (h < 120) {
    r = x;
    g = c;
  } else if (h < 180) {
    g = c;
    b = x;
  } else if (h < 240) {
    g = x;
    b = c;
  } else if (h < 300) {
    r = x;
    b = c;
  } else {
    r = c;
    b = x;
  }
  return {
    R: Math.round((r + m) * 255),
    G: Math.round((g + m) * 255),
    B: Math.round((b + m) * 255),
    isCustom: false,
  };
}

function scaleCell(cell: StripCell, factor: number): StripCell {
  const f = Math.max(0, Math.min(1, factor));
  return {
    R: Math.round(cell.R * f),
    G: Math.round(cell.G * f),
    B: Math.round(cell.B * f),
    isCustom: false,
  };
}

function applyBrightness(cell: StripCell, luminance: number): StripCell {
  return scaleCell(cell, Math.max(0, Math.min(1, luminance / 100)));
}

function sampleRainbowGradient(wrapped: number): StripCell {
  const scaled = wrapped * (RAINBOW_FLOW_STOPS.length - 1);
  const i0 = Math.floor(scaled);
  const i1 = Math.min(RAINBOW_FLOW_STOPS.length - 1, i0 + 1);
  const frac = scaled - i0;
  const a = RAINBOW_FLOW_STOPS[i0];
  const b = RAINBOW_FLOW_STOPS[i1];
  const h = a.h + (b.h - a.h) * frac;
  const s = a.s + (b.s - a.s) * frac;
  const v = a.v + (b.v - a.v) * frac;
  return hsvToRgb(h, s, v);
}

function resolveSpeedFactor(speed: number): number {
  return Math.max(0.2, speed / 50);
}

function isForwardDirection(direction: unknown): boolean {
  return !(direction === "Backward" || direction === 1 || direction === "1");
}

function resolveFlowCellColor(phase: number, singleBase: StripCell): StripCell {
  const beamWidth = 0.22;
  let dist = Math.abs(phase);
  dist = Math.min(dist, 1 - dist);
  const beam = Math.max(0, 1 - dist / beamWidth);
  const floor = 0.14;
  return scaleCell(singleBase, floor + (1 - floor) * beam);
}

function buildStaticFrame(
  base: XingshanStripRoutineBase,
  cols: number
): StripCell[][] {
  const luminance = Number(base.luminance ?? 80);
  const row: StripCell[] = [];
  for (let col = 0; col < cols; col++) {
    const normX = cols <= 1 ? 0.5 : col / (cols - 1);
    row.push(applyBrightness(sampleRainbowGradient(normX), luminance));
  }
  return [row];
}

function buildWaveFrame(
  base: XingshanStripRoutineBase,
  cols: number,
  timeMs: number
): StripCell[][] {
  const luminance = Number(base.luminance ?? 80);
  const speedFactor = resolveSpeedFactor(Number(base.speed ?? 50));
  const dirSign = isForwardDirection(base.direction) ? 1 : -1;
  const scroll = (timeMs / 1000) * speedFactor * 0.55 * dirSign;
  const row: StripCell[] = [];

  for (let col = 0; col < cols; col++) {
    const normX = cols <= 1 ? 0.5 : col / (cols - 1);
    const wrapped = normX + scroll;
    const phase = wrapped - Math.floor(wrapped);
    const color = resolveFlowCellColor(phase, sampleRainbowGradient(normX));
    row.push(applyBrightness(color, luminance));
  }
  return [row];
}

/** 熠熠生辉：整体柔光呼吸 */
function buildGlowFrame(
  base: XingshanStripRoutineBase,
  cols: number,
  timeMs: number
): StripCell[][] {
  const luminance = Number(base.luminance ?? 80);
  const speedFactor = resolveSpeedFactor(Number(base.speed ?? 50));
  const pulse = 0.58 + 0.42 * Math.sin((timeMs / 1000) * speedFactor * 2.4);
  const row: StripCell[] = [];

  for (let col = 0; col < cols; col++) {
    const normX = cols <= 1 ? 0.5 : col / (cols - 1);
    const color = sampleRainbowGradient(normX);
    row.push(applyBrightness(scaleCell(color, pulse), luminance));
  }
  return [row];
}

function buildSparkleFrame(
  base: XingshanStripRoutineBase,
  cols: number,
  timeMs: number
): StripCell[][] {
  const luminance = Number(base.luminance ?? 80);
  const speedFactor = resolveSpeedFactor(Number(base.speed ?? 50));
  const tick = Math.floor((timeMs / 1000) * speedFactor * 6);
  const row: StripCell[] = [];

  for (let col = 0; col < cols; col++) {
    const normX = cols <= 1 ? 0.5 : col / (cols - 1);
    const color = sampleRainbowGradient(normX);
    const sparkle = ((col * 17 + tick * 13) % 7) / 6 > 0.45 ? 1 : 0.12;
    row.push(applyBrightness(scaleCell(color, sparkle), luminance));
  }
  return [row];
}

/** mode=3 流光漫延：从中向左右扩散，类似背光涟漪清漾的一维版 */
function buildFlowExtendFrame(
  base: XingshanStripRoutineBase,
  cols: number,
  timeMs: number
): StripCell[][] {
  const luminance = Number(base.luminance ?? 80);
  const speedFactor = resolveSpeedFactor(Number(base.speed ?? 50));
  const center = (cols - 1) / 2;
  const maxDist = Math.max(center, 1);
  const phase = (timeMs / 1000) * speedFactor * 1.8;
  const row: StripCell[] = [];

  for (let col = 0; col < cols; col++) {
    const dist = Math.abs(col - center) / maxDist;
    const wave = Math.max(
      0,
      Math.sin(phase * Math.PI * 2 - dist * Math.PI * 2.6)
    );
    const intensity = Math.pow(wave, 1.35);
    const normX = cols <= 1 ? 0.5 : col / (cols - 1);
    const color = sampleRainbowGradient(normX);
    row.push(applyBrightness(scaleCell(color, intensity), luminance));
  }
  return [row];
}

function buildOffFrame(cols: number): StripCell[][] {
  const row = Array.from({ length: cols }, () => ({
    R: 0,
    G: 0,
    B: 0,
    isCustom: false,
  }));
  return [row];
}

export function buildXingshanStripRoutinePreviewFrame(
  base: XingshanStripRoutineBase,
  cols: number,
  timeMs: number = performance.now(),
  options?: { dk68FamilyStripPreview?: boolean }
): StripCell[][] {
  const safeCols = Math.max(1, cols);
  if (base.open === "Close") {
    return buildOffFrame(safeCols);
  }

  const mode = Number(base.mode ?? 0);
  if (options?.dk68FamilyStripPreview) {
    switch (mode) {
      case 0:
        return buildWaveFrame(base, safeCols, timeMs);
      case 1:
        return buildGlowFrame(base, safeCols, timeMs);
      case 2:
        return buildFlowExtendFrame(base, safeCols, timeMs);
      case 3:
        return buildStaticFrame(base, safeCols);
      default:
        return buildOffFrame(safeCols);
    }
  }

  switch (mode) {
    case 0:
      return buildStaticFrame(base, safeCols);
    case 1:
      return buildWaveFrame(base, safeCols, timeMs);
    case 2:
      return buildSparkleFrame(base, safeCols, timeMs);
    case 3:
      return buildFlowExtendFrame(base, safeCols, timeMs);
    default:
      return buildOffFrame(safeCols);
  }
}

export function useXingshanStripRoutinePreview(
  enabled: boolean,
  base: XingshanStripRoutineBase | null | undefined,
  cols: number,
  dk68FamilyStripPreview = false
): StripCell[][] | null {
  const [frame, setFrame] = useState<StripCell[][] | null>(null);
  const baseRef = useRef(base);
  baseRef.current = base;
  const dk68PreviewRef = useRef(dk68FamilyStripPreview);
  dk68PreviewRef.current = dk68FamilyStripPreview;

  useEffect(() => {
    if (!enabled || !base || cols <= 0) {
      setFrame(null);
      return;
    }

    let rafId = 0;
    const tick = () => {
      const next = buildXingshanStripRoutinePreviewFrame(
        baseRef.current ?? {},
        cols,
        performance.now(),
        { dk68FamilyStripPreview: dk68PreviewRef.current }
      );
      setFrame(next);
      rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [
    enabled,
    cols,
    dk68FamilyStripPreview,
    base?.open,
    base?.mode,
    base?.speed,
    base?.luminance,
    base?.direction,
  ]);

  return frame;
}
